import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Users, Calendar, User, Settings, LogOut, Crown, 
  MapPin, Heart, MessageCircle, ArrowRight, Clock,
  Video, ChevronRight, Send, ExternalLink, Loader2, CheckCircle, Tag, DollarSign, Ticket, Bell, HelpCircle, Shield, Sparkles
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import ReferralCard from '@/components/ReferralCard';
import DashboardWorkshopCreditCard from '@/components/DashboardWorkshopCreditCard';
import GuestPassCard from '@/components/GuestPassCard';
import FoundingMemberBadge from '@/components/FoundingMemberBadge';
import WorkshopUpgradeUpsell from '@/components/WorkshopUpgradeUpsell';
import MemberSafetyMenu from '@/components/MemberSafetyMenu';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import EventPhotoDisplay from '@/components/EventPhotoDisplay';
import UserAvatar from '@/components/UserAvatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { QRCodeCanvas } from 'qrcode.react';
import api from '@/lib/api';
import NotificationBell from '@/components/NotificationBell';

// Empty state style class
const emptyStateStyle = "font-body text-sm font-normal text-[#4E7070] text-center";

// California city to state mapping
const getCityWithState = (city) => {
  if (!city) return '';
  return `${city}, CA`;
};

export default function DashboardPage() {
  const { user, logout, updateUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [matches, setMatches] = useState([]);
  const [circle, setCircle] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [circleRequest, setCircleRequest] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestFormData, setRequestFormData] = useState({
    preferred_days: [], preferred_format: 'both', notes: ''
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [upgradePromoOpen, setUpgradePromoOpen] = useState(false);
  const [upgradePromoCode, setUpgradePromoCode] = useState('');
  const [upgradePromoLoading, setUpgradePromoLoading] = useState(false);
  const [eventCheckoutLoading, setEventCheckoutLoading] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [showTickets, setShowTickets] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSubject, setHelpSubject] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpSending, setHelpSending] = useState(false);
  const [myHelpRequests, setMyHelpRequests] = useState([]);
  const [helpHistoryLoading, setHelpHistoryLoading] = useState(false);
  const [openThread, setOpenThread] = useState(null); // {id, subject, replies, status, ...}
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadReply, setThreadReply] = useState('');
  const [threadSending, setThreadSending] = useState(false);

  useEffect(() => {
    // Handle payment success return from Stripe
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    // Handle event payment return
    const eventPayment = searchParams.get('event_payment');
    const eventPaymentId = searchParams.get('payment_id');
    const eventId = searchParams.get('event_id');
    
    if (eventPayment === 'success' && eventPaymentId && eventId) {
      confirmEventPayment(eventId, eventPaymentId);
    } else if (eventPayment === 'cancelled') {
      toast.info('Event payment was cancelled');
      window.history.replaceState({}, '', '/dashboard');
    } else if (paymentStatus === 'success' && !paymentProcessed) {
      handlePaymentSuccess();
    } else if (sessionId && !paymentProcessed) {
      checkPaymentStatus(sessionId);
    }
    
    fetchData();
  }, []);

  const handlePaymentSuccess = async () => {
    setPaymentProcessed(true);
    
    try {
      // Refresh user data to get updated membership tier
      await refreshUser();
      const userResponse = await api.get('/me');
      const updatedUser = userResponse.data;
      updateUser(updatedUser);
      
      // Determine the tier name for the message
      const tierName = updatedUser.membership_tier === 'thrive' ? 'Thrive' : 
                       updatedUser.membership_tier === 'connect' ? 'Connect' : 
                       updatedUser.membership_tier;
      
      // Show success message
      if (tierName && tierName !== 'free' && tierName !== 'gather') {
        toast.success(`Welcome to ${tierName}! Your membership is now active.`, {
          duration: 5000
        });
      } else {
        toast.success('Payment successful! Your membership is now active.', {
          duration: 5000
        });
      }
      
      // Clean URL without refreshing page
      window.history.replaceState({}, '', '/dashboard');
      
    } catch (error) {
      console.error('Error refreshing user after payment:', error);
      toast.success('Payment successful! Welcome to the community.');
      window.history.replaceState({}, '', '/dashboard');
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5 || paymentProcessed) return;
    
    try {
      const response = await api.get(`/stripe/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentProcessed(true);
        const userResponse = await api.get('/me');
        const updatedUser = userResponse.data;
        updateUser(updatedUser);
        
        const tierName = updatedUser.membership_tier === 'thrive' ? 'Thrive' : 
                         updatedUser.membership_tier === 'connect' ? 'Connect' : 
                         updatedUser.membership_tier;
        
        toast.success(`Welcome to ${tierName}! Your membership is now active.`, {
          duration: 5000
        });
        window.history.replaceState({}, '', '/dashboard');
        fetchData();
      } else if (response.data.status !== 'expired') {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment check error:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [matchesRes, circleRes, eventsRes, unreadRes] = await Promise.all([
        api.get('/matches'),
        api.get('/circles/mine'),
        api.get('/events'),
        api.get('/messages/unread-count')
      ]);
      const matchesData = matchesRes.data?.matches || matchesRes.data || [];
      setMatches(Array.isArray(matchesData) ? matchesData : []);
      setCircle(circleRes.data);
      setEvents(eventsRes.data.slice(0, 3));
      setUnreadMessages(unreadRes.data?.unread_count || 0);

      // Fetch tickets
      try {
        const ticketsRes = await api.get('/events/my-tickets');
        setMyTickets(ticketsRes.data || []);
      } catch { /* no tickets */ }
      
      // Check for pending circle request (Thrive members not in circle)
      const tier = user?.membership_tier;
      if ((tier === 'premium' || tier === 'thrive') && !circleRes.data) {
        try {
          const reqRes = await api.get('/circles/request/mine');
          setCircleRequest(reqRes.data);
        } catch { /* no request */ }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get compatibility badge color based on label
  const getCompatibilityBadge = (label) => {
    if (!label) return null;
    const colors = {
      'Great match': 'bg-teal text-white',
      'Strong match': 'bg-seafoam/40 text-teal',
      'Good match': 'bg-cream text-navy border border-seafoam'
    };
    return colors[label] || 'bg-gray-100 text-gray-600';
  };

  const handleRSVP = async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/rsvp`);
      setEvents(events.map(e => 
        e.id === eventId ? { ...e, user_rsvped: response.data.rsvped } : e
      ));
      toast.success(response.data.rsvped ? "You're in!" : 'RSVP cancelled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to RSVP');
    }
  };

  const handleEventCheckout = async (eventId) => {
    setEventCheckoutLoading(eventId);
    try {
      const response = await api.post(`/events/${eventId}/checkout`);
      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.action === 'credit_applied') {
        toast.success(response.data.message);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setEventCheckoutLoading(null);
    }
  };

  const confirmEventPayment = async (eventId, paymentId) => {
    try {
      const res = await api.post(`/events/${eventId}/confirm-payment`, { payment_id: paymentId });
      if (res.data.status === 'confirmed' || res.data.status === 'already_confirmed') {
        toast.success("You're confirmed! Your ticket has been purchased.");
      } else {
        toast.info('Payment is being processed. You will be confirmed shortly.');
      }
    } catch (error) {
      toast.error('Could not confirm your event payment. Please contact support.');
    }
    window.history.replaceState({}, '', '/dashboard');
    fetchData();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openHelpModal = async () => {
    setHelpOpen(true);
    setOpenThread(null);
    setHelpHistoryLoading(true);
    try {
      const res = await api.get('/help/requests');
      setMyHelpRequests(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMyHelpRequests([]);
    }
    setHelpHistoryLoading(false);
  };

  const openHelpThread = async (helpId) => {
    setThreadLoading(true);
    setOpenThread({ id: helpId, loading: true });
    try {
      const res = await api.get(`/help/requests/${helpId}`);
      setOpenThread(res.data);
    } catch {
      toast.error('Failed to load conversation');
      setOpenThread(null);
    }
    setThreadLoading(false);
  };

  const refreshHelpList = async () => {
    try {
      const res = await api.get('/help/requests');
      setMyHelpRequests(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  const submitHelpRequest = async () => {
    if (!helpMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setHelpSending(true);
    try {
      await api.post('/help/contact', {
        subject: helpSubject.trim(),
        message: helpMessage.trim(),
      });
      toast.success("Your message was sent. We'll get back to you soon!");
      setHelpSubject('');
      setHelpMessage('');
      await refreshHelpList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send. Please try again.');
    }
    setHelpSending(false);
  };

  const submitThreadReply = async () => {
    if (!threadReply.trim() || !openThread?.id) return;
    setThreadSending(true);
    try {
      await api.post(`/help/requests/${openThread.id}/reply`, { message: threadReply.trim() });
      setThreadReply('');
      // Refresh the thread
      const res = await api.get(`/help/requests/${openThread.id}`);
      setOpenThread(res.data);
      refreshHelpList();
      toast.success('Follow-up sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    }
    setThreadSending(false);
  };

  const handleCircleRequest = async () => {
    setSubmittingRequest(true);
    try {
      await api.post('/circles/request', requestFormData);
      toast.success('Your request has been received! We will be in touch within 3-5 days.');
      setShowRequestForm(false);
      setCircleRequest({ status: 'pending' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const toggleDay = (day) => {
    setRequestFormData(prev => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day)
        ? prev.preferred_days.filter(d => d !== day)
        : [...prev.preferred_days, day]
    }));
  };

  // 2-tier model: Connect (free) / Thrive (paid). Legacy aliases mapped.
  const tierBadge = {
    free: { label: 'Connect', class: 'bg-gray-100 text-gray-600' },
    gather: { label: 'Connect', class: 'bg-gray-100 text-gray-600' },
    connect: { label: 'Connect', class: 'bg-gray-100 text-gray-600' },
    connected: { label: 'Thrive', class: 'bg-teal text-white' },
    premium: { label: 'Thrive', class: 'bg-teal text-white' },
    thrive: { label: 'Thrive', class: 'bg-teal text-white' }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <div className="animate-pulse text-teal text-lg">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream font-body">
      {/* Header */}
      <header className="bg-white border-b border-border-subtle sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-xl text-navy">
            Cali<span className="italic-teal">Moms</span> Connect
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <span className="text-sm text-teal font-semibold">Dashboard</span>
            <Link to="/messages" className="text-sm text-text-mid hover:text-teal flex items-center gap-1 relative">
              Messages
              {unreadMessages > 0 && (
                <span className="absolute -top-2 -right-4 w-5 h-5 bg-teal rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </Link>
            <Link to="/community" className="text-sm text-text-mid hover:text-teal">Community Board</Link>
            <Link to="/my-city" className="text-sm text-text-mid hover:text-teal">My City</Link>
            <Link to="/wellness" className="text-sm text-text-mid hover:text-teal" data-testid="wellness-nav-link">Wellness</Link>
          </nav>
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Link to="/admin/dashboard" data-testid="admin-nav-link">
                <Button size="sm" className="bg-navy hover:bg-navy/80 text-white rounded-full text-xs px-4">
                  Admin Dashboard
                </Button>
              </Link>
            )}
            {!user?.is_admin && user?.can_manage_events && (
              <Link to="/coadmin/dashboard" data-testid="coadmin-nav-link" title="Switch to Events Co-Admin Dashboard">
                <Button size="sm" className="bg-teal hover:bg-teal-deep text-white rounded-full text-xs px-4 gap-1">
                  <Shield size={12} /> Co-Admin Dashboard
                </Button>
              </Link>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={openHelpModal}
              data-testid="help-contact-btn"
              className="hidden sm:inline-flex border-teal text-teal hover:bg-teal hover:text-white rounded-full text-xs px-3 gap-1"
            >
              <HelpCircle size={14} /> Need Help?
            </Button>
            <button
              onClick={openHelpModal}
              data-testid="help-contact-icon-btn"
              className="sm:hidden text-text-mid hover:text-teal"
              aria-label="Need help?"
            >
              <HelpCircle size={20} />
            </button>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-text-mid hover:text-teal" data-testid="settings-menu-trigger" aria-label="Settings">
                  <Settings size={20} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem asChild>
                  <Link to="/profile/edit" data-testid="settings-edit-profile-link">Edit Profile</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings/privacy" data-testid="settings-privacy-link">Privacy &amp; Blocked</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={handleLogout} className="text-text-mid hover:text-red-500">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="dashboard-main">
        {/* Welcome Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-2">
            <h1 className="font-display text-3xl md:text-4xl text-navy">
              Welcome back, {user?.first_name}
            </h1>
            <Badge className={tierBadge[user?.membership_tier]?.class}>
              {tierBadge[user?.membership_tier]?.label}
            </Badge>
            {user?.is_founding_member && <FoundingMemberBadge />}
          </div>
          <p className="text-text-mid flex items-center gap-2">
            <MapPin size={16} /> {getCityWithState(user?.city)}
          </p>
        </motion.div>

        {/* Refer a Friend card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {/* Workshop credit widget (Thrive) OR upsell (Connect) */}
          {user?.membership_tier === 'thrive' ? (
            <DashboardWorkshopCreditCard />
          ) : (
            <WorkshopUpgradeUpsell />
          )}
          <ReferralCard />
        </motion.div>

        {/* Annual guest pass status (renders only when a pass exists) */}
        <div className="mb-8">
          <GuestPassCard />
        </div>

        {/* Trial Status Banner */}
        {user?.trial_status === 'active' && user?.trial_end && (() => {
          const trialEnd = new Date(user.trial_end);
          const now = new Date();
          const daysLeft = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
          const chargeDate = trialEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-teal/10 border border-teal/20 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
              data-testid="trial-banner"
            >
              <div>
                <p className="text-navy font-medium text-sm">
                  You're in your free trial — <span className="font-bold text-teal">{daysLeft} days remaining</span>.
                  Your card will be charged $29/month on {chargeDate}.
                </p>
              </div>
              <button
                onClick={async (e) => {
                  const btn = e.currentTarget;
                  btn.textContent = 'Opening...';
                  btn.disabled = true;
                  try {
                    const res = await api.post('/stripe/portal');
                    if (res.data?.url) window.location.href = res.data.url;
                    else if (res.data?.action === 'upgrade') {
                      navigate('/pricing');
                      btn.textContent = 'Manage Billing';
                      btn.disabled = false;
                    }
                  } catch (err) {
                    btn.textContent = 'Manage Billing';
                    btn.disabled = false;
                    if (err.code === 'ERR_NETWORK' || !err.response) {
                      toast.error('Network error. Please check your connection and try again.');
                    } else {
                      toast.error(err.response?.data?.detail || 'Unable to open billing.');
                    }
                  }
                }}
                className="text-teal text-xs font-semibold underline underline-offset-2 whitespace-nowrap hover:text-teal-deep"
                data-testid="trial-manage-billing"
              >
                Manage Billing
              </button>
            </motion.div>
          );
        })()}

        {/* Promo Access Banner */}
        {user?.promo_status === 'active' && user?.promo_end && (() => {
          const promoEnd = new Date(user.promo_end);
          const now = new Date();
          const daysLeft = Math.max(0, Math.ceil((promoEnd - now) / (1000 * 60 * 60 * 24)));
          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-6"
              data-testid="promo-banner"
            >
              <p className="text-navy font-medium text-sm">
                You are enjoying complimentary Thrive access — <span className="font-bold text-purple-700">{daysLeft} days remaining</span>. We hope you love it!
              </p>
            </motion.div>
          );
        })()}

        {/* Dashboard Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* My Matches */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 bg-white rounded-[24px] p-6 shadow-card"
            data-testid="matches-card"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl text-navy flex items-center gap-2">
                <Heart className="text-teal" size={20} />
                My Matches
              </h2>
            </div>

            {['free', 'gather', 'connect'].includes(user?.membership_tier) ? (
              <div className="text-center py-12">
                <p className={emptyStateStyle + " mb-4"}>Thrive includes one workshop every month, priority matching with moms in your area, and 30% off additional workshops — all for $39/month.</p>
                <Link to="/pricing">
                  <Button className="bg-teal hover:bg-teal-deep text-white rounded-full">
                    Upgrade to Thrive <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            ) : matches.length === 0 ? (
              <div className="text-center py-12 bg-cream/50 rounded-2xl">
                <div className="w-16 h-16 bg-seafoam/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-teal" size={24} />
                </div>
                <p className={emptyStateStyle}>Your matches are being curated</p>
                <p className={emptyStateStyle + " mt-2"}>We'll notify you by email!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {matches.map((match, index) => (
                  <div
                    key={match.user?.id || index}
                    className="p-4 bg-cream/50 rounded-2xl hover:bg-cream transition-colors relative"
                  >
                    {match.user?.id && (
                      <div className="absolute top-2 right-2">
                        <MemberSafetyMenu
                          userId={match.user.id}
                          userName={match.user.first_name || 'this member'}
                          onBlocked={() => setMatches((prev) => prev.filter((m) => m.user?.id !== match.user.id))}
                          size="sm"
                        />
                      </div>
                    )}
                    <div className="flex items-start gap-4">
                      <UserAvatar user={match.user} size="w-14 h-14" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-navy">
                            {match.user?.first_name} {match.user?.last_name?.[0]}.
                          </p>
                          {match.label && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getCompatibilityBadge(match.label)}`}>
                              {match.label}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-text-mid flex items-center gap-1">
                          <MapPin size={12} /> {match.user?.city}
                          {match.proximity && (
                            <span className="text-teal-deep ml-1">• {match.proximity}</span>
                          )}
                        </p>
                        {match.user?.bio && (
                          <p className="text-sm text-text-mid mt-2 line-clamp-2">{match.user.bio}</p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {match.shared_interests?.slice(0, 3).map(interest => (
                            <span key={interest} className="text-xs bg-teal/20 text-teal-deep px-2 py-0.5 rounded-full">
                              {interest}
                            </span>
                          ))}
                          {match.user?.topics_of_interest?.slice(0, 2).map(topic => (
                            <span key={topic} className="text-xs bg-seafoam/50 text-teal px-2 py-0.5 rounded-full">
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end mt-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/messages?to=${match.user?.id}`)}
                        className="rounded-full border-teal text-teal hover:bg-teal hover:text-white"
                        data-testid={`message-match-${match.user?.id}`}
                      >
                        <Send size={16} className="mr-1" /> Send a Hello
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* My Circle (Premium only) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-[24px] p-6 shadow-card"
            data-testid="circle-card"
          >
            <h2 className="font-display text-xl text-navy flex items-center gap-2 mb-6">
              <Crown className="text-teal" size={20} />
              My Circle
            </h2>

            {user?.membership_tier !== 'premium' && user?.membership_tier !== 'thrive' ? (
              <div className="text-center py-8">
                <p className={emptyStateStyle + " mb-4"}>Thrive members get one workshop free every month, plus private Circles of 4–6 moms — $39/month.</p>
                <Link to="/pricing">
                  <Button variant="outline" size="sm" className="rounded-full border-teal text-teal">
                    Upgrade to Thrive
                  </Button>
                </Link>
              </div>
            ) : !circle ? (
              /* No circle yet — show request flow */
              circleRequest?.status === 'pending' ? (
                <div className="text-center py-6 bg-cream/50 rounded-2xl px-4">
                  <CheckCircle className="mx-auto text-teal mb-3" size={32} />
                  <p className="font-semibold text-navy mb-1">Request Received!</p>
                  <p className={emptyStateStyle}>We're curating your circle. You'll hear from us within 3-5 days.</p>
                </div>
              ) : circleRequest?.status === 'waitlisted' ? (
                <div className="text-center py-6 bg-cream/50 rounded-2xl px-4">
                  <Clock className="mx-auto text-amber-500 mb-3" size={32} />
                  <p className="font-semibold text-navy mb-1">On the Waitlist</p>
                  <p className={emptyStateStyle}>We're matching you with the perfect group. Hang tight!</p>
                </div>
              ) : showRequestForm ? (
                <div className="space-y-4">
                  <p className="text-sm text-text-mid">Help us match you with the right group:</p>
                  <div>
                    <p className="text-sm font-medium text-navy mb-2">Preferred days</p>
                    <div className="flex flex-wrap gap-2">
                      {['Weekday mornings', 'Weekday evenings', 'Weekends'].map(day => (
                        <button
                          key={day}
                          onClick={() => toggleDay(day)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            requestFormData.preferred_days.includes(day)
                              ? 'bg-teal text-white border-teal'
                              : 'border-seafoam text-text-mid hover:border-teal'
                          }`}
                          data-testid={`day-${day.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-navy mb-2">Preferred format</p>
                    <div className="flex gap-2">
                      {[{v:'zoom',l:'Zoom'},{v:'in_person',l:'In-Person'},{v:'both',l:'Both'}].map(opt => (
                        <button
                          key={opt.v}
                          onClick={() => setRequestFormData(p => ({...p, preferred_format: opt.v}))}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            requestFormData.preferred_format === opt.v
                              ? 'bg-teal text-white border-teal'
                              : 'border-seafoam text-text-mid hover:border-teal'
                          }`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea
                    value={requestFormData.notes}
                    onChange={(e) => setRequestFormData(p => ({...p, notes: e.target.value}))}
                    placeholder="Any notes for us? (optional)"
                    rows={2}
                    className="rounded-xl border-seafoam text-sm"
                    data-testid="circle-request-notes"
                  />
                  <Button
                    onClick={handleCircleRequest}
                    disabled={submittingRequest}
                    className="w-full bg-teal hover:bg-teal-deep text-white rounded-full"
                    data-testid="submit-circle-request-btn"
                  >
                    {submittingRequest ? <><Loader2 size={16} className="mr-1 animate-spin" /> Sending...</> : 'Send My Request'}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6 bg-gradient-to-br from-seafoam/20 to-teal/10 rounded-2xl px-4">
                  <Crown className="mx-auto text-teal mb-3" size={32} />
                  <p className="font-semibold text-navy mb-2">You're eligible for a Circle!</p>
                  <p className={emptyStateStyle + " mb-4"}>A small, intimate group of moms curated just for you.</p>
                  <Button
                    onClick={() => setShowRequestForm(true)}
                    className="bg-teal hover:bg-teal-deep text-white rounded-full"
                    data-testid="request-circle-btn"
                  >
                    Request Circle Placement
                  </Button>
                </div>
              )
            ) : (
              <div className="space-y-4">
                <p className="font-semibold text-navy">{circle.name}</p>
                <div className="flex -space-x-2">
                  {circle.members?.slice(0, 6).map((member) => (
                    <div key={member.id} className="border-2 border-white rounded-full">
                      <UserAvatar user={member} size="w-10 h-10" />
                    </div>
                  ))}
                </div>
                {/* Show next event */}
                {circle.events?.[0] && (
                  <div className="bg-cream rounded-xl p-4">
                    <p className="text-sm text-text-mid flex items-center gap-2">
                      {circle.events[0].event_type === 'zoom' ? <Video size={14} className="text-teal" /> : <MapPin size={14} className="text-teal" />}
                      Next: {circle.events[0].title}
                    </p>
                    <p className="font-semibold text-navy text-sm">
                      {new Date(circle.events[0].event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                    {circle.events[0].zoom_link && (
                      <a href={circle.events[0].zoom_link} target="_blank" rel="noopener noreferrer" className="text-sm text-teal hover:text-teal-deep font-medium inline-flex items-center gap-1 mt-1">
                        <ExternalLink size={12} /> Join Zoom
                      </a>
                    )}
                  </div>
                )}
                <Link to="/circle">
                  <Button variant="outline" size="sm" className="w-full rounded-full border-teal text-teal hover:bg-teal hover:text-white" data-testid="view-circle-btn">
                    View Circle <ChevronRight size={14} className="ml-1" />
                  </Button>
                </Link>
              </div>
            )}
          </motion.div>

          {/* Upcoming Events */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2 bg-white rounded-[24px] p-6 shadow-card"
            data-testid="events-card"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl text-navy flex items-center gap-2">
                <Calendar className="text-teal" size={20} />
                Upcoming Events
              </h2>
            </div>

            {events.length === 0 ? (
              <p className={emptyStateStyle + " py-8"}>No upcoming events</p>
            ) : (
              <div className="space-y-4">
                {events.map(event => (
                  <div 
                    key={event.id}
                    className="flex items-center gap-4 p-4 bg-cream/50 rounded-2xl cursor-pointer hover:bg-cream/80 transition-colors"
                    onClick={() => navigate(`/events/${event.id}`)}
                    data-testid={`dashboard-event-row-${event.id}`}
                  >
                    <EventPhotoDisplay event={event} variant="thumb" testIdPrefix={`dashboard-event-${event.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-navy">{event.title}</p>
                      <p className="text-sm text-text-mid flex items-center gap-1">
                        <MapPin size={12} /> {event.city} • {event.location}
                      </p>
                      {(() => {
                        const isWorkshop = (event.event_type || '').toLowerCase() === 'workshop';
                        const w = event.workshop || {};
                        if (isWorkshop && w.pricing_label === 'workshop_free_credit') {
                          return (
                            <p className="text-sm font-semibold text-teal-deep mt-0.5 flex items-center gap-1">
                              <Sparkles size={12} /> Free with monthly credit
                            </p>
                          );
                        }
                        if (isWorkshop && w.pricing_label === 'workshop_member_30off') {
                          return (
                            <p className="text-sm font-semibold text-navy mt-0.5 flex items-center gap-1">
                              <DollarSign size={12} /> ${(w.effective_price_cents / 100).toFixed(2)}{' '}
                              <span className="text-text-mid text-xs line-through">${Number(w.base_price).toFixed(2)}</span>
                              <span className="text-xs text-teal-deep">member price</span>
                            </p>
                          );
                        }
                        if (isWorkshop) {
                          return (
                            <p className="text-sm font-semibold text-navy mt-0.5 flex items-center gap-1">
                              <DollarSign size={12} /> ${Number(w.base_price || event.base_price || 0).toFixed(2)} — Thrive members attend free
                            </p>
                          );
                        }
                        if (event.is_paid) {
                          return (
                            <p className="text-sm font-semibold text-navy mt-0.5 flex items-center gap-1">
                              <DollarSign size={12} /> ${Number(event.price).toFixed(2)} per person
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="flex flex-col items-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const isWorkshop = (event.event_type || '').toLowerCase() === 'workshop';
                        const w = event.workshop || {};
                        if (isWorkshop && !event.user_rsvped && !event.user_has_paid) {
                          let label = 'Register';
                          let cls = 'bg-navy hover:bg-navy/90 text-white';
                          if (w.pricing_label === 'workshop_free_credit') { label = 'Register — free'; cls = 'bg-teal hover:bg-teal-deep text-white'; }
                          else if (w.pricing_label === 'workshop_member_30off') { label = `Register $${(w.effective_price_cents / 100).toFixed(2)}`; }
                          else { label = `Register $${Number(w.base_price || event.base_price || 0).toFixed(0)}`; }
                          return (
                            <Button
                              onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.id}`); }}
                              size="sm"
                              className={`rounded-full ${cls}`}
                              data-testid={`workshop-cta-dashboard-${event.id}`}
                            >
                              <Ticket size={14} className="mr-1" />
                              {label}
                            </Button>
                          );
                        }
                        if (event.is_paid && !event.user_has_paid && !event.user_rsvped) {
                          return (
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleEventCheckout(event.id); }}
                              disabled={eventCheckoutLoading === event.id}
                              size="sm"
                              className="rounded-full bg-navy hover:bg-navy/90 text-white"
                              data-testid={`checkout-event-${event.id}`}
                            >
                              {eventCheckoutLoading === event.id ? (
                                <Loader2 size={14} className="animate-spin mr-1" />
                              ) : (
                                <DollarSign size={14} className="mr-1" />
                              )}
                              Buy Ticket
                            </Button>
                          );
                        }
                        return (
                          <Button
                            onClick={(e) => { e.stopPropagation(); if (!event.is_paid && !isWorkshop) handleRSVP(event.id); }}
                            variant={event.user_rsvped || event.user_has_paid ? 'default' : 'outline'}
                            size="sm"
                            className={`rounded-full ${
                              event.user_rsvped || event.user_has_paid
                                ? 'bg-teal hover:bg-teal-deep text-white' 
                                : 'border-teal text-teal hover:bg-teal hover:text-white'
                            } ${(event.is_paid || isWorkshop) && (event.user_has_paid || event.user_rsvped) ? 'cursor-default' : ''}`}
                            data-testid={`rsvp-event-${event.id}`}
                          >
                            {event.user_rsvped || event.user_has_paid ? 'Going' : 'RSVP'}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                ))}
                {events.some(e => e.is_paid) && (
                  <p className="text-xs text-text-mid text-center mt-3 italic" data-testid="all-sales-final-note">
                    All sales are final. Contact the organizer for assistance.
                  </p>
                )}
              </div>
            )}
          </motion.div>

          {/* My Tickets */}
          {myTickets.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="lg:col-span-2 bg-white rounded-[24px] p-6 shadow-card"
              data-testid="my-tickets-card"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl text-navy flex items-center gap-2">
                  <Tag className="text-teal" size={20} />
                  My Tickets ({myTickets.length})
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTickets(!showTickets)}
                  className="text-teal text-sm"
                  data-testid="toggle-tickets-btn"
                >
                  {showTickets ? 'Hide' : 'Show All'}
                </Button>
              </div>

              <div className="space-y-4">
                {(showTickets ? myTickets : myTickets.slice(0, 2)).map(ticket => (
                  <div
                    key={ticket.id}
                    className="flex items-center gap-4 p-4 bg-cream/50 rounded-2xl border border-dashed border-teal/30"
                    data-testid={`ticket-${ticket.ticket_code}`}
                  >
                    <div className="flex-shrink-0">
                      <QRCodeCanvas
                        value={ticket.ticket_code || ''}
                        size={72}
                        fgColor="#0d3b66"
                        bgColor="transparent"
                        level="M"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-navy">{ticket.event?.title || 'Event'}</p>
                      <p className="text-sm text-text-mid flex items-center gap-1">
                        <Calendar size={12} />
                        {ticket.event?.event_date ? new Date(ticket.event.event_date).toLocaleDateString('en-US', {
                          weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                        }) : 'TBD'}
                      </p>
                      <p className="text-sm text-text-mid flex items-center gap-1">
                        <MapPin size={12} /> {ticket.event?.city} {ticket.event?.location ? `• ${ticket.event.location}` : ''}
                      </p>
                      <p className="text-xs font-mono text-teal mt-1">{ticket.ticket_code}</p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-navy text-white">${(ticket.amount_cents / 100).toFixed(2)}</Badge>
                      {ticket.checked_in && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1 justify-end">
                          <CheckCircle size={12} /> Checked in
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-text-mid text-center mt-3 italic">
                Show your QR code at the door for check-in
              </p>
            </motion.div>
          )}

          {/* Profile Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-[24px] p-6 shadow-card"
            data-testid="profile-card"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl text-navy flex items-center gap-2">
                <User className="text-teal" size={20} />
                My Profile
              </h2>
              <Link to="/profile/edit" className="text-teal text-sm font-medium hover:text-teal-deep">
                Edit
              </Link>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="mb-4">
                <UserAvatar user={user} size="w-20 h-20" textSize="text-xl" />
              </div>
              <p className="font-semibold text-navy">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-text-mid">{getCityWithState(user?.city)}</p>
              
              {user?.bio && (
                <p className="text-sm text-text-mid mt-4 line-clamp-3">{user.bio}</p>
              )}

              {/* Topics of Interest */}
              {user?.topics_of_interest?.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-center mt-4">
                  {user.topics_of_interest.slice(0, 4).map(topic => (
                    <span key={topic} className="text-xs bg-seafoam/50 text-teal px-2 py-1 rounded-full">
                      {topic}
                    </span>
                  ))}
                </div>
              )}

              {/* Ideal Hangouts */}
              {user?.ideal_hangout?.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-center mt-2">
                  {user.ideal_hangout.slice(0, 2).map(h => (
                    <span key={h} className="text-xs bg-teal/10 text-teal-deep px-2 py-1 rounded-full">
                      {h}
                    </span>
                  ))}
                </div>
              )}

              {/* Social Style & Work Status */}
              <div className="flex gap-2 mt-3 text-xs text-text-light">
                {user?.social_style && (
                  <span className="capitalize">{user.social_style}</span>
                )}
                {user?.social_style && user?.work_status && <span>•</span>}
                {user?.work_status && (
                  <span className="capitalize">{user.work_status.replace(/_/g, ' ')}</span>
                )}
              </div>
            </div>
          </motion.div>

          {/* Membership Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="lg:col-span-3 bg-navy rounded-[24px] p-6 shadow-card"
            data-testid="membership-card"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-white/70 text-sm mb-1">Current Plan</p>
                <p className="text-white font-display text-2xl">
                  {tierBadge[user?.membership_tier]?.label}
                </p>
              </div>
              <div className="flex gap-3">
                {user?.membership_tier !== 'premium' && (
                  <Link to="/pricing">
                    <Button className="bg-teal hover:bg-teal-deep text-white rounded-full">
                      Upgrade <ChevronRight size={16} />
                    </Button>
                  </Link>
                )}
                <Button 
                  variant="outline" 
                  className="rounded-full border-white/30 text-white hover:bg-white/10"
                  data-testid="manage-billing-btn"
                  onClick={async (e) => {
                    const btn = e.currentTarget;
                    const originalText = btn.textContent;
                    btn.textContent = 'Opening...';
                    btn.disabled = true;
                    try {
                      const res = await api.post('/stripe/portal');
                      if (res.data?.url) {
                        window.location.href = res.data.url;
                      } else if (res.data?.action === 'upgrade') {
                        toast.info(res.data.message, { duration: 5000 });
                        navigate('/pricing');
                        btn.textContent = originalText;
                        btn.disabled = false;
                      }
                    } catch (err) {
                      btn.textContent = originalText;
                      btn.disabled = false;
                      if (err.response?.status === 401) {
                        toast.error('Session expired. Please log in again.');
                        navigate('/login?redirect=/dashboard&action=billing');
                      } else if (err.code === 'ERR_NETWORK' || !err.response) {
                        toast.error('Network error. Please check your internet connection and try again.');
                      } else {
                        toast.error(err.response?.data?.detail || 'Unable to open billing portal. Please try again.');
                      }
                    }
                  }}
                >
                  Manage Billing
                </Button>
              </div>
            </div>

            {/* Promo Code Entry inside membership card */}
            {user?.promo_status !== 'active' && (
              <div className="mt-4 pt-4 border-t border-white/10 text-center">
                {!upgradePromoOpen ? (
                  <button
                    onClick={() => setUpgradePromoOpen(true)}
                    className="text-white/50 text-sm hover:text-white/80 transition-colors inline-flex items-center gap-1.5"
                    data-testid="dashboard-promo-link"
                  >
                    <Tag size={14} /> Have a promo code? Click here
                  </button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-sm mx-auto"
                    data-testid="dashboard-promo-form"
                  >
                    <p className="text-white font-semibold text-sm mb-3 text-center">Enter your promo code.</p>
                    <div className="flex gap-2">
                      <input
                        value={upgradePromoCode}
                        onChange={(e) => setUpgradePromoCode(e.target.value.toUpperCase())}
                        placeholder="e.g. CMCUPGRADE"
                        className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-white/40 rounded-xl px-4 py-2 text-sm font-mono text-center tracking-wider focus:outline-none focus:border-teal"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && upgradePromoCode.trim()) {
                            document.querySelector('[data-testid="dashboard-apply-promo-btn"]')?.click();
                          }
                        }}
                        data-testid="dashboard-promo-input"
                      />
                      <Button
                        disabled={upgradePromoLoading || !upgradePromoCode.trim()}
                        className="bg-teal hover:bg-teal-deep text-white rounded-full px-5"
                        data-testid="dashboard-apply-promo-btn"
                        onClick={async () => {
                          setUpgradePromoLoading(true);
                          try {
                            const res = await api.post('/promo/apply', { code: upgradePromoCode.trim() });
                            toast.success(res.data.message);
                            setUpgradePromoOpen(false);
                            setUpgradePromoCode('');
                            refreshUser();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Invalid promo code');
                          } finally {
                            setUpgradePromoLoading(false);
                          }
                        }}
                      >
                        {upgradePromoLoading ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                    <button
                      onClick={() => { setUpgradePromoOpen(false); setUpgradePromoCode(''); }}
                      className="text-white/40 text-xs hover:text-white/60 mt-2 inline-block"
                    >
                      Cancel
                    </button>
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Help / Contact Modal */}
      <Dialog open={helpOpen} onOpenChange={(open) => { setHelpOpen(open); if (!open) setOpenThread(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="help-modal">
          {!openThread ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-navy flex items-center gap-2">
                  <HelpCircle size={20} className="text-teal" /> Need Help? Contact Us
                </DialogTitle>
                <DialogDescription className="text-sm text-text-mid">
                  Send us a message and we'll get back to you as soon as possible.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <label className="text-xs font-semibold text-navy">Subject (optional)</label>
                  <Input
                    value={helpSubject}
                    onChange={(e) => setHelpSubject(e.target.value)}
                    placeholder="e.g. Trouble with my membership"
                    data-testid="help-subject-input"
                    maxLength={120}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-navy">How can we help?<span className="text-red-500"> *</span></label>
                  <Textarea
                    value={helpMessage}
                    onChange={(e) => setHelpMessage(e.target.value)}
                    placeholder="Tell us what's going on..."
                    rows={5}
                    data-testid="help-message-input"
                    maxLength={2000}
                  />
                  <div className="text-xs text-text-mid text-right mt-1">{helpMessage.length}/2000</div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setHelpOpen(false)}
                  className="rounded-full"
                  data-testid="help-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitHelpRequest}
                  disabled={helpSending || !helpMessage.trim()}
                  className="bg-teal hover:bg-teal-deep text-white rounded-full"
                  data-testid="help-send-btn"
                >
                  {helpSending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
                  Send Message
                </Button>
              </DialogFooter>

              {/* Previous requests */}
              <div className="mt-4 pt-4 border-t border-border-subtle">
                <h4 className="text-sm font-semibold text-navy mb-2">Your Previous Requests</h4>
                {helpHistoryLoading ? (
                  <div className="flex justify-center py-3"><Loader2 size={18} className="animate-spin text-teal" /></div>
                ) : myHelpRequests.length === 0 ? (
                  <p className="text-xs text-text-mid">No previous help requests.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="help-history-list">
                    {myHelpRequests.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => openHelpThread(r.id)}
                        className="w-full text-left bg-beige/40 hover:bg-beige/70 transition rounded-lg p-3 text-xs"
                        data-testid={`help-history-item-${r.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-navy">{r.subject || 'Help Request'}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] capitalize">{(r.status || 'open').replace('_', ' ')}</Badge>
                            <span className="text-[10px] text-text-mid">{(r.replies?.length || 1)} msg{(r.replies?.length || 1) !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <p className="text-text-mid mt-1 line-clamp-2">{r.message}</p>
                        <p className="text-[10px] text-text-mid mt-1">
                          {r.created_at ? new Date(r.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setOpenThread(null)}
                    className="text-text-mid hover:text-teal p-1"
                    aria-label="Back"
                    data-testid="thread-back-btn"
                  >
                    <ChevronRight size={18} className="rotate-180" />
                  </button>
                  <DialogTitle className="font-display text-navy flex-1">
                    {openThread.subject || 'Help Request'}
                  </DialogTitle>
                  {openThread.status && (
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {(openThread.status || 'open').replace('_', ' ')}
                    </Badge>
                  )}
                </div>
              </DialogHeader>
              {threadLoading ? (
                <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-teal" /></div>
              ) : (
                <div className="space-y-3 max-h-[50vh] overflow-y-auto" data-testid="help-thread-list">
                  {(openThread.replies || []).map((m) => (
                    <div
                      key={m.id}
                      className={`rounded-xl p-3 text-sm ${m.from_admin ? 'bg-teal/5 border-l-4 border-teal' : 'bg-beige/40'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-semibold ${m.from_admin ? 'text-teal' : 'text-navy'}`}>
                          {m.from_admin ? 'CaliMoms Support' : (m.author_name || 'You')}
                        </span>
                        <span className="text-[10px] text-text-mid">
                          {m.created_at ? new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap">{m.message}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-border-subtle">
                <Textarea
                  value={threadReply}
                  onChange={(e) => setThreadReply(e.target.value)}
                  placeholder="Add a follow-up message..."
                  rows={3}
                  data-testid="thread-reply-input"
                  maxLength={2000}
                />
                <div className="flex justify-end gap-2 mt-2">
                  <Button
                    onClick={submitThreadReply}
                    disabled={threadSending || !threadReply.trim()}
                    className="bg-teal hover:bg-teal-deep text-white rounded-full"
                    data-testid="thread-reply-send-btn"
                  >
                    {threadSending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
                    Send Reply
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
